import {
  type ItineraryComponentOut,
  type MissingField,
  type PublishItineraryResponse,
} from '@utazzo/shared';
import { UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { CalendarDays, Check, Copy, Eye, MapPin, Send, ShieldAlert, Users, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useCheckPublishPrices,
  useItineraryComponents,
  useItineraryDays,
  useItineraryDetail,
  useItineraryPreviewLink,
  usePublishItinerary,
} from '../use-itineraries';
import { useApproveReprice, useRejectReprice } from '../use-reprice-approvals';

import { ComponentCard, isFdLandPackage, isFdSupplement } from './ComponentCard';
import { PricingSidebar } from './PricingSidebar';
import { PublishPriceCheckModal } from './PublishPriceCheckModal';

import { useCurrentUser } from '@/features/auth/use-auth';
import { LeadMissingFieldsModal } from '@/features/leads/components/LeadMissingFieldsModal';
import { useLead } from '@/features/leads/use-leads';
import { extractServerError } from '@/lib/api-error';

/**
 * Preview (Phase 5). A read-only brochure of the whole itinerary - the day-by-
 * day plan with rich component cards (no edit controls) plus the live pricing
 * summary - the final review before publishing/sending.
 */

const noop = (): void => undefined;

const fmtDate = (iso?: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
      })
    : null;

/** Best-effort clipboard copy; fails silently when the tab isn't focused (e.g. after a modal). */
const copyShareLink = async (url: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
};

export const PreviewTab = ({
  itineraryId,
  onGoToPricing,
}: {
  itineraryId: string;
  /** Jump back to the Pricing step (used by the "Reprice again" prompt). */
  onGoToPricing?: () => void;
}): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const days = useItineraryDays(itineraryId).data ?? [];
  const components = useItineraryComponents(itineraryId).data ?? [];
  const checkPrices = useCheckPublishPrices(itineraryId);
  const publish = usePublishItinerary(itineraryId);
  const previewLink = useItineraryPreviewLink(itineraryId);
  const me = useCurrentUser();
  const approveReprice = useApproveReprice();
  const rejectReprice = useRejectReprice();
  const [freshPublish, setFreshPublish] = useState<PublishItineraryResponse | null>(null);
  const [publishPhase, setPublishPhase] = useState<'idle' | 'checking' | 'review' | 'confirming'>(
    'idle',
  );
  const [priceCheck, setPriceCheck] = useState<Awaited<
    ReturnType<typeof checkPrices.mutateAsync>
  > | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Set when the server refuses publish with REPRICE_INVALIDATED (backstop for
  // the up-front block below - e.g. prices moved between the price check and
  // Confirm). Forces the modal into its blocking "Reprice again" state.
  const [serverRepriceInvalidated, setServerRepriceInvalidated] = useState(false);
  // Reject requires a remark - opened from the inline Reject button below.
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState('');
  // Travel-intent backlog gate (2026-08-10). When publish fails with the
  // typed 400 `active_needs_travel_intent`, we open LeadMissingFieldsModal
  // scoped to just the travel-intent fields; on save success we
  // auto-re-fire the pending publish with its original repriceToken.
  const [travelIntentGap, setTravelIntentGap] = useState<{
    missing: MissingField[];
    pendingRepriceToken?: string;
  } | null>(null);
  const lead = useLead(detail?.leadId ?? null).data ?? null;

  // A reprice back-solves an exact target total; it is only valid while the
  // quote is unchanged. When one is active AND the live-price check found a
  // delta, the target no longer holds - block publish and prompt to reprice.
  const repriceActive = detail?.repriceTargetPaise != null;
  const repriceBlocked =
    serverRepriceInvalidated || (repriceActive && priceCheck?.status === 'CHANGED');

  // Reprice approval lock (Task 7c). A >2%-of-margin discount reprice (Task
  // 6) writes a PENDING row, routed through the lead reviewer hierarchy - the
  // repriced version cannot be published or shared until it clears. This is
  // independent of (and composes with) `repriceBlocked` above: that one fires
  // mid-publish when live prices moved under an active reprice; this one
  // blocks the CTA up-front while a human decision is outstanding.
  const repriceApproval = detail?.repriceApproval ?? null;
  const repriceApprovalPending = repriceApproval?.status === 'PENDING';
  // Mirrors the server's `assertCanReviewRepriceApproval`: ADMIN reviews any
  // row; the assigned SALES_LEAD reviews their own; the requesting agent (and
  // everyone else) only sees the lock, no Approve / Reject.
  const canReviewReprice =
    repriceApprovalPending &&
    !!me &&
    (me.role === 'ADMIN' ||
      (me.role === 'SALES_LEAD' && repriceApproval?.assignedReviewerId === me.id));
  // Only people involved in THIS approval see the pending banner: the reviewer
  // (with Approve/Reject) or the requester who submitted it (lock only, so they
  // know why publish is blocked). Unrelated viewers see nothing - it is not
  // their approval to see or act on.
  const isRepriceRequester = !!me && me.id === repriceApproval?.requestedById;
  const showRepriceApprovalBanner =
    repriceApprovalPending && (canReviewReprice || isRepriceRequester);

  const closeRejectDialog = (): void => {
    setRejectDialogOpen(false);
    setRejectRemark('');
  };

  const onApproveReprice = (): void => {
    if (!repriceApproval) return;
    approveReprice.mutate({ id: repriceApproval.id });
  };

  const onRejectReprice = (): void => {
    if (!repriceApproval || !rejectRemark.trim()) return;
    rejectReprice.mutate(
      { id: repriceApproval.id, remark: rejectRemark.trim() },
      { onSuccess: closeRejectDialog },
    );
  };

  // Source of truth for "is this itinerary already published?" is the DETAIL
  // record (currentPublishedRevisionId + shareUrl), NOT this session's mutation
  // result. Otherwise revisiting a published itinerary shows the "Publish" CTA
  // again and clicking it minted duplicate revisions on the same row.
  const publishedVersion =
    freshPublish?.publishedVersion ?? detail?.currentPublishedVersion ?? null;
  const shareUrl = freshPublish?.shareUrl ?? detail?.shareUrl ?? null;
  const alreadyPublished =
    publishedVersion !== null && publishedVersion !== undefined && !!shareUrl;
  // ADR-0065. Quote ID of the current published version (from the detail
  // record once refetched). Shown alongside the version - which is the
  // "Quote No." - on the published confirmation line.
  const publishedQuoteId = detail?.currentQuoteId ?? null;

  const finishPublish = (res: PublishItineraryResponse): void => {
    setFreshPublish(res);
    setPublishPhase('idle');
    setPriceCheck(null);
    setPublishError(null);
    setServerRepriceInvalidated(false);
    if (res.shareUrl) {
      void copyShareLink(res.shareUrl).then((copied) => {
        if (copied) toast.success('Link copied');
      });
    }
  };

  const runPublish = (repriceToken?: string): void => {
    setPublishError(null);
    if (repriceToken) setPublishPhase('confirming');
    publish.mutate(repriceToken ? { repriceToken } : {}, {
      onSuccess: (res) => finishPublish(res),
      onError: (err: unknown) => {
        const extracted = extractServerError(err);
        // The server refused because an active reprice is no longer valid.
        // Flip the modal into its blocking "Reprice again" state.
        if (extracted.code === 'REPRICE_INVALIDATED') {
          setServerRepriceInvalidated(true);
          setPublishPhase('review');
          setPublishError(extracted.message);
          return;
        }
        // Travel-intent backlog gate (2026-08-10). The lead is ACTIVE but
        // still missing travel-intent fields. Open the missing-fields
        // modal scoped to those; on save success we replay this publish
        // with the same repriceToken, so the operator does not lose the
        // in-flight reprice.
        if (extracted.code === 'active_needs_travel_intent') {
          const missing = ((err as { missing?: MissingField[] })?.missing ?? []) as MissingField[];
          setTravelIntentGap({ missing, pendingRepriceToken: repriceToken });
          setPublishPhase(priceCheck ? 'review' : 'idle');
          return;
        }
        setPublishPhase(priceCheck ? 'review' : 'idle');
        setPublishError(extracted.message ?? 'Publish failed - please try again.');
      },
    });
  };

  const onPublish = (): void => {
    setPublishError(null);
    if (detail?.isLongHaul) {
      const pending = components.some(
        (c) => (c.kind === 'ACTIVITY' || c.kind === 'TRANSFER') && (Number(c.costPaise) || 0) === 0,
      );
      if (pending) {
        toast.error(
          'Enter net prices on every activity and transfer in Day-by-day before publishing.',
        );
        return;
      }
    }
    setServerRepriceInvalidated(false);
    setPublishPhase('checking');
    checkPrices.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === 'UNCHANGED') {
          runPublish();
          return;
        }
        setPriceCheck(result);
        setPublishPhase('review');
      },
      onError: (err: unknown) => {
        setPublishPhase('idle');
        const extracted = extractServerError(err);
        const raw =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code?: unknown }).code)
            : '';
        const msg = extracted.message ?? '';
        const timedOut =
          raw === 'ECONNABORTED' ||
          /timeout of \d+ms exceeded/i.test(msg) ||
          /timed out/i.test(msg);
        toast.error(
          timedOut
            ? 'Price check timed out while talking to the flight/hotel supplier. Please try again — slow legs are skipped after 12s.'
            : msg || 'Could not verify latest prices before publish.',
        );
      },
    });
  };

  const onConfirmPublish = (): void => {
    // Publishing over an invalidated reprice is blocked - operator must reprice.
    if (repriceBlocked) return;
    if (!priceCheck?.repriceToken) return;
    runPublish(priceCheck.repriceToken);
  };

  /** Abort this publish attempt and take the operator to the Pricing step. */
  const onRepriceAgain = (): void => {
    setPublishPhase('idle');
    setPriceCheck(null);
    setPublishError(null);
    setServerRepriceInvalidated(false);
    if (onGoToPricing) onGoToPricing();
    else toast.info('Open the Pricing step to reprice this itinerary.');
  };

  /**
   * Open the customer view of the CURRENT draft in a new tab. Publishes
   * nothing - no version, no share link, no customer email, no payment link.
   *
   * The tab is opened synchronously on the click and only navigated once the
   * URL arrives; opening it in the mutation callback instead would be treated
   * as a popup by the browser and blocked.
   *
   * No `noopener` in the feature string on purpose: with it, window.open
   * returns null by spec, so we would open a tab we can never navigate and
   * leave the operator staring at about:blank. The opener is severed on the
   * child instead, right before it navigates.
   */
  const onPreview = (): void => {
    const tab = window.open('', '_blank');
    previewLink.mutate(undefined, {
      onSuccess: (res) => {
        if (!res.pricingSaved) {
          toast.warning('Pricing is not saved yet - the preview shows pre-markup amounts.');
        }
        if (tab) {
          tab.opener = null;
          tab.location.replace(res.previewUrl);
          tab.focus();
          return;
        }
        // Popup blocker ate the tab - hand the operator the link instead of
        // silently doing nothing.
        void copyShareLink(res.previewUrl).then((copied) =>
          toast.info(
            copied
              ? 'Preview link copied - paste it in a new tab (popup was blocked).'
              : `Popup blocked. Preview link: ${res.previewUrl}`,
            { duration: 10_000 },
          ),
        );
      },
      onError: () => tab?.close(),
    });
  };

  const copyLink = (): void => {
    if (!shareUrl) return;
    void copyShareLink(shareUrl).then((copied) => {
      if (copied) toast.success('Link copied');
      else toast.error('Could not copy - click the link field and copy manually.');
    });
  };

  const byDay = useMemo(() => {
    const map = new Map<string, ItineraryComponentOut[]>();
    for (const c of components) {
      // The fixed-departure land package carries the trip cost but is not shown
      // as a day component (see DaysTab).
      if (isFdLandPackage(c)) continue;
      // Vehicle supplements are unscheduled add-ons shown in their own section
      // below, not on a day (same as the Days tab).
      if (isFdSupplement(c)) continue;
      if (!c.dayId) continue;
      const arr = map.get(c.dayId) ?? [];
      arr.push(c);
      map.set(c.dayId, arr);
    }
    return map;
  }, [components]);

  const fdSupplements = useMemo(() => components.filter(isFdSupplement), [components]);

  const travellers = detail
    ? `${detail.adultsCount} Adult${detail.adultsCount === 1 ? '' : 's'}${
        detail.childrenCount
          ? ` · ${detail.childrenCount} Child${detail.childrenCount === 1 ? '' : 'ren'}`
          : ''
      }${detail.infantsCount ? ` · ${detail.infantsCount} Infant${detail.infantsCount === 1 ? '' : 's'}` : ''}`
    : '';

  const publishBusy =
    publishPhase === 'checking' || publishPhase === 'confirming' || publish.isPending;

  return (
    <>
      {/* Phones: explicit minmax(0,1fr) track so wide content can't stretch
          the implicit column past the screen. */}
      <div className="grid items-start gap-6 pb-20 max-sm:grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          {/* Brochure header */}
          <section className="rounded-xl border border-zinc-200 bg-white p-5">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
              {detail?.title ?? 'Itinerary'}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
              {detail?.travelStartDate && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                  {fmtDate(detail.travelStartDate)}
                  {detail.travelEndDate ? ` - ${fmtDate(detail.travelEndDate)}` : ''}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                {detail?.numberOfNights ?? 0}N · {detail?.numberOfDays ?? 0}D
              </span>
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                {travellers}
              </span>
            </div>
          </section>

          {/* Reprice approval lock (Task 7c). Shown whenever a discount
              reprice is awaiting a manager decision - composes with the
              Publish / share section below by disabling its CTAs rather than
              replacing them, so the operator still sees what publishing will
              eventually do once the reprice clears. */}
          {showRepriceApprovalBanner && (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-amber-900">
                    {canReviewReprice
                      ? 'This repriced version needs your approval before it can be published or shared.'
                      : "Pending your manager's approval - this repriced version cannot be published or shared until approved."}
                  </p>
                  {canReviewReprice && (
                    <div className="flex flex-wrap items-center gap-2">
                      <UzButton
                        size="sm"
                        onClick={onApproveReprice}
                        loading={approveReprice.isPending}
                      >
                        <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Approve
                      </UzButton>
                      <UzButton
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectDialogOpen(true)}
                        disabled={approveReprice.isPending}
                      >
                        <X className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Reject
                      </UzButton>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Publish / share */}
          <section className="border-primary/20 bg-accent/40 rounded-lg border p-4">
            {alreadyPublished ? (
              <div className="space-y-2">
                <p className="text-primary flex items-center gap-1.5 text-sm font-medium">
                  <Check className="h-4 w-4" aria-hidden />
                  Published v{publishedVersion} (Quote No.)
                  {publishedQuoteId ? ` - ${publishedQuoteId}` : ''} - share this link with the
                  customer:
                </p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl ?? ''}
                    className="flex-1 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-700"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <UzButton variant="outline" size="sm" onClick={copyLink}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Copy
                  </UzButton>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Ready to send?</p>
                  <p className="text-xs text-zinc-500">
                    Publishing checks the latest flight & hotel prices, then freezes this version
                    and generates a public link for the customer to review. Use Preview first to see
                    the customer's view - it sends nothing.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <UzButton
                    variant="outline"
                    onClick={onPreview}
                    loading={previewLink.isPending}
                    disabled={days.length === 0 || repriceApprovalPending}
                    title={repriceApprovalPending ? 'Pending reprice approval' : undefined}
                  >
                    <Eye className="mr-1.5 h-4 w-4" aria-hidden />
                    Preview
                  </UzButton>
                  <UzButton
                    onClick={onPublish}
                    loading={publishBusy}
                    disabled={days.length === 0 || repriceApprovalPending}
                    title={repriceApprovalPending ? 'Pending reprice approval' : undefined}
                  >
                    <Send className="mr-1.5 h-4 w-4" aria-hidden />
                    {publishPhase === 'checking'
                      ? 'Checking latest prices…'
                      : 'Publish & get share link'}
                  </UzButton>
                </div>
              </div>
            )}
          </section>

          {days.length === 0 && (
            <p className="rounded-lg border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-400">
              Nothing to preview yet - build the day-by-day plan first.
            </p>
          )}

          {days.map((day) => {
            const dayComps = byDay.get(day.id) ?? [];
            return (
              <section key={day.id} className="rounded-lg border border-zinc-200 bg-white">
                <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-100 px-5 py-3">
                  <span className="bg-primary text-primary-foreground inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums">
                    {day.dayIndex}
                  </span>
                  <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                    {day.title ?? `Day ${day.dayIndex}`}
                  </h3>
                  {day.destinationCityName && (
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                      <MapPin className="h-3 w-3 text-zinc-400" aria-hidden />
                      {day.destinationCityName}
                    </span>
                  )}
                  {fmtDate(day.date) && (
                    <span className="text-xs text-zinc-500">{fmtDate(day.date)}</span>
                  )}
                </header>
                <div className="space-y-2 px-5 py-4 max-sm:px-3">
                  {/* Day gallery. The first image is the day's hero; the rest
                      follow as thumbnails. Fixed departures seed these from the
                      template, and a hand-built itinerary can carry them too. */}
                  {day.imageUrls.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <img
                        src={day.imageUrls[0]}
                        alt={day.title ?? `Day ${day.dayIndex}`}
                        loading="lazy"
                        className="h-48 w-full rounded-lg object-cover"
                      />
                      {day.imageUrls.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {day.imageUrls.slice(1).map((url) => (
                            <img
                              key={url}
                              src={url}
                              alt=""
                              loading="lazy"
                              className="h-16 w-24 shrink-0 rounded-md object-cover"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {day.briefHtml && (
                    <p className="mb-3 text-sm leading-relaxed whitespace-pre-line text-zinc-600">
                      {day.briefHtml}
                    </p>
                  )}
                  {dayComps.length === 0 ? (
                    <p className="text-xs text-zinc-400">No items.</p>
                  ) : (
                    dayComps.map((c) => (
                      <ComponentCard
                        key={c.id}
                        component={c}
                        editable={false}
                        onEdit={noop}
                        onRemove={noop}
                        dayDate={day.date}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}

          {fdSupplements.length > 0 && (
            <section className="rounded-lg border border-zinc-200 bg-white">
              <header className="border-b border-zinc-100 px-5 py-3.5">
                <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                  Vehicle supplements
                </h3>
              </header>
              <div className="space-y-2 px-5 py-4 max-sm:px-3">
                {fdSupplements.map((c) => (
                  <ComponentCard
                    key={c.id}
                    component={c}
                    editable={false}
                    onEdit={noop}
                    onRemove={noop}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <PricingSidebar itineraryId={itineraryId} />
      </div>

      {(publishPhase === 'review' || publishPhase === 'confirming') && priceCheck && (
        <PublishPriceCheckModal
          check={priceCheck}
          confirming={publishPhase === 'confirming'}
          error={publishError}
          onConfirm={onConfirmPublish}
          repriceBlocked={repriceBlocked}
          onRepriceAgain={onRepriceAgain}
        />
      )}

      {/* Reject requires a remark (Task 7c) - the assigned reviewer / admin
          confirms here before the reprice is cleared. */}
      <UzDialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeRejectDialog();
        }}
        title="Reject reprice"
        description="Rejecting clears the reprice: the solved discount is zeroed and the itinerary returns to its pre-reprice quote. A remark is required so the seller knows why."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={closeRejectDialog}
              disabled={rejectReprice.isPending}
            >
              Cancel
            </UzButton>
            <UzButton
              variant="outline"
              onClick={onRejectReprice}
              loading={rejectReprice.isPending}
              disabled={!rejectRemark.trim()}
            >
              Confirm rejection
            </UzButton>
          </div>
        }
      >
        <UzTextarea
          name="rejectRemark"
          label="Reason for rejection (required)"
          rows={3}
          value={rejectRemark}
          onChange={(e) => setRejectRemark(e.target.value)}
          disabled={rejectReprice.isPending}
        />
      </UzDialog>

      {/* Travel-intent backlog gate. Opens when publish 400'd with
          `active_needs_travel_intent`. The operator fills the six
          travel-intent fields inline, we swallow the modal, and re-fire
          the pending publish with the same repriceToken so nothing is
          lost. Silent no-op while lead is still loading. */}
      {travelIntentGap && lead && (
        <LeadMissingFieldsModal
          open
          mode="travelIntent"
          lead={lead}
          onOpenChange={(open) => {
            if (!open) setTravelIntentGap(null);
          }}
          title="Fill travel-intent fields before publishing"
          description="This lead is missing information we now require before sending an itinerary to the customer."
          onReady={() => {
            const pendingToken = travelIntentGap.pendingRepriceToken;
            setTravelIntentGap(null);
            runPublish(pendingToken);
          }}
        />
      )}
    </>
  );
};
